/**
 * Shared Component properties for accessing/mutating the current user
 */

import { isArray, isUndefined, filter } from 'lodash'

var CurrentUserMixin = {
  computed: {
    /**
     * Provides a short hand to similarly named user module getter. Returns
     * the UserRecord representing the current user
     *
     * @reutrn UserRecord|undefined
     */
    user () {
      return this.$store.getters['userModule/getUser']
    },

    /**
     * @return Boolean
     */
    userLoaded () {
      return this.$store.getters['userModule/getUser'].id !== undefined
    },

    /**
     * @return Array
     */
    customers () {
      if (!isUndefined(this.user.customers) && isArray(this.user.customers)) {
        return this.user.customers
      }
      return []
    },

    /**
     * Provides a short hand to similarly named user module getter. Returns
     * a boolean flag identifying if a customer context selected.
     *
     * @reutrn Boolean
     */
    hasCustomerContextId () {
      return this.$store.getters['userModule/hasCustomerContextId']
    },

    /**
     * Provides a short hand to similarly named user module getter. Returns
     * an integer representing the current customer context (even if the
     * CustomerRecord for the selected context isn't fully loaded)
     *
     * @reutrn Integer|undefined
     */
    customerContextId () {
      return this.$store.getters['userModule/customerContextId']
    },

    /**
     * Provides a short hand to similarly named user module getter. Returns
     * a boolean flag identifying if the customer context is fully populated.
     * Unlike hasCustomerContextId above (which returns true regardless of
     * whether or not the CustomerRecord is populated)
     *
     * @reutrn Boolean|undefined
     */
    hasCustomerContext () {
      return this.$store.getters['userModule/hasCustomerContext']
    },

    /**
     * Provides a short hand to similarly named user module getter. Returns
     * the CustomerRecord representing the current customer context
     *
     * @reutrn CustomerRecord|undefined
     */
    customerContext () {
      return this.$store.getters['userModule/customerContext']
    },

    /**
     *
     * @returns {*}
     */
    customerTimezone () {
      return this.customerContext.timezone()
    },
    /**
     * Compute a list of related customers.
     * Will return a list of all available customers that are siblings,
     * parents, or children of the currently selected customer.
     *
     * @return {Array}
     */
    relatedCustomers () {
      return filter(this.customers, (c) => {
        return (c.parent_id === this.customerContext.parent_id ||
                c.parent_id === this.customerContext.id ||
                c.id === this.customerContext.parent_id)
      })
    },

    /**
     * Compute a list of regular (non-parent-level) customers that
     * the current user has access to.
     * @return array
     */
    regularCustomers () {
      return filter(this.customers, c => c.parent_id !== null)
    },

    /**
     * Determine if the current user is a root user.
     */
    isRootUser () {
      return filter(this.user.roles, { code: 'root' }).length !== 0
    }
  },
  watch: {
    /**
     * Fire the customerContextChangeHandler if/when the customer context is
     * changed
     *
     * @param CustomerRecord newCustomerContext
     * @param CustomerRecord oldCustomerContext
     */
    customerContext: function (newCustomerContext, oldCustomerContext) {
      // verify the context has actually changed from one customer to another
      // (not just a refresh of the existing customer record)
      if (newCustomerContext.id !== oldCustomerContext.id &&
          typeof this.customerContextChangeHandler === 'function') {
        this.customerContextChangeHandler(newCustomerContext, oldCustomerContext)
      }
    }
  }
}

export default CurrentUserMixin
